import React, { Suspense, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  // ** -------------- Protected Routes -------------- **
  // ** Onboarding Route **
  OnboardingRoute,
  // ** Admin Route **
  AdminRoute,
  // ** Super Admin Route **
  SuperAdminRoute,
  // ** Marketing Admin Route **
  MarketingAdminRoute,
  // ** Profile Route **
  ProfileRoute,
} from "@routes";
// ** Redux Actions **
import { isUserAction } from "@redux/actions/authActions";
import {
  // ** -------------- Auth views --------------
  Signup,
  AccountActivation,
  Login,
  ForgotPassword,
  NewPassword,
  Terms,
  Privacy,
  Ethics,
  AppSumoSelect,
  ExtensionAuthSuccess,
  ExtensionAuth,
  VerifyAccountAction,
  VerifyEmail,
  TeamInviteAccept,
  // ** -------------- Onboarding views -------------- ** //
  Onboarding,
  Subscribe,
  // ** -------------- Profile Views -------------- ** //
  // Profile
  MyProfile,
  SubscriptionPlans,
  MySubscription,
  ChangeLanguage,
  MySecurity,
  MyPassword,
  // ** -------------- Super Admin views -------------- ** //
  SuperAdminDashboard,
  // Admins
  AdminList,
  AdminDetails,
  AdminUsageHistory,
  AdminSubscriptionHistory,
  AdminPaymentHistory,
  // Visitors
  VisitorList,
  // Leads
  LeadsList,
  // Feedbacks
  FeedbacksList,
  // Prompts
  PromptsList,
  // Constants
  ConstantsList,
  // Announcements
  AnnouncementsList,
  // Copilots - Unified Dashboard
  CopilotsDashboard,
  // Legacy Copilot Views (kept for backwards compatibility)
  CopilotCategoriesList,
  CopilotsList,
  CopilotChatsList,
  MobileCopilotChatCategoriesList,
  MobileCopilotChatsList,
  // Blogs
  // Blog Categories
  BlogCategoriesList,
  // Blog Tags
  BlogTagsList,
  // Blogs List
  BlogsList,
  BlogCreate,
  BlogUpdate,
  // Blog Campaigns
  BlogCampaignsList,
  BlogCampaignCreate,
  BlogCampaignProgress,
  // Settings
  AiConfig,
  // Plans
  PlansList,
  PlanCreate,
  PlanUpdate,
  // Coupons
  CouponsList,
  CouponCreate,
  CouponUpdate,
  // Promotions
  PromotionsList,
  PromotionCreate,
  PromotionUpdate,
  PromotionAnalytics,
  PromotionsAnalyticsDashboard,

  // ---- Credit Rewards ----
  CreditConfig,
  CreditAnalytics,

  // ---- Analytics Dashboard ----
  SuperAdminAnalyticsDashboard,

  // ---- Emails ----
  EmailAnalyticsDashboard,
  // Email Campaigns
  EmailCampaignsList,
  EmailCampaignCreate,
  EmailCampaignUpdate,
  EmailCampaignAnalytics,
  EmailCampaignSentEmails,
  // HTML Templates
  HtmlTemplatesList,
  HtmlTemplateBuilder,
  // User Email Management
  UserEmailList,
  UserEmailCreate,
  UserEmailUpdate,
  SmtpList,
  SmtpCreate,
  SmtpUpdate,
  EmailDomainCreate,
  EmailDomainUpdate,
  EmailDomainList,

  // Email Sequences
  EmailSequenceList,
  EmailSequenceCreate,
  EmailSequenceUpdate,
  EmailSequenceAnalytics,

  // ---- Pulse Reports ----
  PulseReportsAnalyticsDashboard,
  PulseReportsList,
  PulseReportCreate,
  PulseReportEdit,
  PulseReportStats,
  PulseReportRecipients,
  PulseHtmlTemplatesList,
  PulseHtmlTemplateBuilder,

  // ---- SEO ----
  // AI Apps
  AiAppsList,
  AiAppCreate,
  AiAppUpdate,
  AiAppCategoriesList,
  // AI Glossary
  AiGlossaryList,
  AiGlossaryCreate,
  AiGlossaryUpdate,
  AiGlossaryCategoriesList,
  // AI Tools
  AiToolsList,
  AiToolCreate,
  AiToolUpdate,
  AiToolCategoriesList,
  // Programmatic SEO
  ProgrammaticSeoCampaigns,
  ProgrammaticSeoDashboard,
  ProgrammaticSeoAnalytics,
  ProgrammaticSeoPages,

  // ** -------------- Marketing Admin views -------------- ** //
  // Dashboard
  MarketingAdminDashboard,

  // ** -------------- Admin views -------------- ** //
  // Dashboard
  AdminDashboard,
  // History
  GenerationHistoryList,
  ChatHistoryList,
  GenerationDetails,
  ChatDetails,
  // Trash
  GenerationTrashList,
  ChatTrashList,
  // Team
  TeamList,
  TeamAnalytics,
  // Prompts
  AdminPromptsList,
  // Chats
  Chats,
  // Subscription
  SubscriptionSuccess,
  PaymentCancel,
  // Copilotly Apps
  CopilotlyApps,
  // ** -------------- Wildcard -------------- ** //
  RandomPageRedirect,
} from "@views";
import { getValue, setValue } from "@utils/localStorage";
import { useTranslation } from "react-i18next";

const App = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { i18n } = useTranslation();

  // Create a QueryClient instance
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  });

  // Determine if the current path is an auth route
  const isAuthRoute =
    [
      "/",
      "/signup",
      "/login",
      "/verify-email",
      "/account/activate",
      "/account/action/verify",
      "/forgot-password",
      "/new-password",
      "/privacy",
      "/terms",
      "/ethics",
      "/team/join",
      "/extension/auth",
      "/extension/auth/success",
    ].includes(history.location.pathname) ||
    history.location.pathname.startsWith("/appsumo/select/signup");

  // If local storage "copin" is true and is on the auth pages page, redirect to dashboard
  useEffect(() => {
    if (
      localStorage.getItem("copin") === "true" &&
      (history.location.pathname === "/" ||
        history.location.pathname === "/signup" ||
        history.location.pathname === "/login" ||
        history.location.pathname === "/team/join" ||
        history.location.pathname.startsWith("/appsumo/select/signup"))
    ) {
      // Dispatch the user action
      dispatch(isUserAction({ history }));
    }
  }, [dispatch, history]);

  // set overflow as hidden to all pages instead of profile pages
  useEffect(() => {
    // set isOverflow based on whether the current route starts with "/profile" or "/subscribe".
    const isOverflow =
      history?.location.pathname.startsWith("/profile") ||
      history?.location.pathname.startsWith("/onboarding") ||
      history?.location.pathname.startsWith("/subscribe") ||
      history?.location.pathname.startsWith("/super-admin/email") ||
      history?.location.pathname.startsWith("/super-admin/promotions") ||
      history?.location.pathname.startsWith("/super-admin/credits");

    const bodyOverflow = isOverflow ? "auto" : "hidden";

    // Set overflow Property on the body element
    document.body.style.overflowY = bodyOverflow;
    document.body.style.overflowX = "hidden";

    // Cleanup the effect
    return () => {
      document.body.style.overflowY = "hidden";
      document.body.style.overflowX = "auto";
    };
  }, [history?.location.pathname]);

  // fetch selected language
  useEffect(() => {
    const fetchLanguage = async () => {
      const selectedLanguage = await getValue("language");

      if (selectedLanguage) {
        i18n.changeLanguage(selectedLanguage);
        setValue("language", selectedLanguage);
      }
    };

    fetchLanguage();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        <Suspense
          fallback={
            <>
              <div
                class="loader"
                style={{ color: "purple", width: "50px" }}
              ></div>
            </>
          }
        >
          {!isAuthRoute ? (
            <>
              <ProtectedRoutes />
            </>
          ) : (
            <AuthRoutes user={user} />
          )}
        </Suspense>
      </div>
    </QueryClientProvider>
  );
};

// Auth Routes
const AuthRoutes = ({ user }) => {
  return (
    <Switch>
      {/*************** Common Routes ***************/}
      <Route exact path="/" component={!user && Login} />
      <Route exact path="/login" component={!user && Login} />
      <Route exact path="/signup" component={!user && Signup} />
      <Route exact path="/verify-email" component={!user && VerifyEmail} />
      <Route
        exact
        path="/appsumo/select/signup/:email"
        component={!user ? AppSumoSelect : null}
      />
      <Route
        exact
        path="/account/activate"
        component={!user && AccountActivation}
      />
      <Route
        exact
        path="/account/action/verify"
        component={VerifyAccountAction}
      />
      <Route
        exact
        path="/forgot-password"
        component={!user && ForgotPassword}
      />
      <Route exact path="/new-password" component={!user && NewPassword} />
      <Route exact path="/privacy" component={!user && Privacy} />
      <Route exact path="/ethics" component={!user && Ethics} />
      <Route exact path="/terms" component={!user && Terms} />
      <Route exact path="/team/join" component={TeamInviteAccept} />

      {/* Extension Auth (handles both logged in and not logged in cases) */}
      <Route exact path="/extension/auth" component={ExtensionAuth} />
      {/* Extension Auth Success (for OAuth popup) */}
      <Route exact path="/extension/auth/success" component={ExtensionAuthSuccess} />

      {/* Wildcard */}
      <Route path={"*"} component={RandomPageRedirect} />
    </Switch>
  );
};

// Protected Routes
const ProtectedRoutes = () => {
  return (
    <Switch>
      {/*************** Onboarding Routes ***************/}
      {/* Onboarding */}
      <OnboardingRoute exact path="/onboarding" component={Onboarding} />
      {/* Subscribe */}
      <OnboardingRoute exact path="/subscribe" component={Subscribe} />
      {/*************** Super Admin Routes ***************/}
      <SuperAdminRoute
        exact
        path="/super-admin/dashboard"
        component={SuperAdminDashboard}
      />
      {/* Admins */}
      <SuperAdminRoute exact path="/super-admin/admins" component={AdminList} />
      <SuperAdminRoute
        exact
        path="/super-admin/admin/:id"
        component={AdminDetails}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/admin/:id/usage-history"
        component={AdminUsageHistory}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/admin/:id/subscription-history"
        component={AdminSubscriptionHistory}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/admin/:id/payment-history"
        component={AdminPaymentHistory}
      />
      {/* Visitors */}
      <SuperAdminRoute
        exact
        path="/super-admin/visitors"
        component={VisitorList}
      />
      {/* Leads */}
      <SuperAdminRoute exact path="/super-admin/leads" component={LeadsList} />
      {/* Feedbacks */}
      <SuperAdminRoute
        exact
        path="/super-admin/feedbacks"
        component={FeedbacksList}
      />
      {/* Prompts */}
      <SuperAdminRoute
        exact
        path="/super-admin/prompts"
        component={PromptsList}
      />
      {/* Blogs */}
      {/* Blog Categories */}
      <SuperAdminRoute
        exact
        path="/super-admin/blogs/blog-categories"
        component={BlogCategoriesList}
      />
      {/* Blog Tags */}
      <SuperAdminRoute
        exact
        path="/super-admin/blogs/tags"
        component={BlogTagsList}
      />
      {/* Blogs List */}
      <SuperAdminRoute exact path="/super-admin/blogs" component={BlogsList} />
      <SuperAdminRoute
        exact
        path="/super-admin/blog/create"
        component={BlogCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/blog/:id/update"
        component={BlogUpdate}
      />
      {/* Blog Campaigns */}
      <SuperAdminRoute
        exact
        path="/super-admin/blog-campaigns"
        component={BlogCampaignsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/blog-campaign/create"
        component={BlogCampaignCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/blog-campaign/:id/progress"
        component={BlogCampaignProgress}
      />

      {/* Emails */}
      {/* Email Analytics Dashboard */}
      <SuperAdminRoute
        exact
        path="/super-admin/email/analytics"
        component={EmailAnalyticsDashboard}
      />
      {/* Email Campaigns */}
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-campaigns"
        component={EmailCampaignsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-campaigns/create"
        component={EmailCampaignCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-campaigns/:id/update"
        component={EmailCampaignUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-campaigns/:id/analytics"
        component={EmailCampaignAnalytics}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-campaigns/:id/sent-emails"
        component={EmailCampaignSentEmails}
      />
      {/* HTML Templates */}
      <SuperAdminRoute
        exact
        path="/super-admin/email/html-templates"
        component={HtmlTemplatesList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/html-template/:id/create"
        component={HtmlTemplateBuilder}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/html-template/:id/update"
        component={HtmlTemplateBuilder}
      />

      {/* User Email Management Views */}
      <SuperAdminRoute
        exact
        path="/super-admin/email/users"
        component={UserEmailList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/users/create"
        component={UserEmailCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/users/:id/update"
        component={UserEmailUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/smtp"
        component={SmtpList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/smtp/create"
        component={SmtpCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/smtp/:id/update"
        component={SmtpUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/domain/create"
        component={EmailDomainCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/domain/:id/update"
        component={EmailDomainUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/domain"
        component={EmailDomainList}
      />

      {/* Email Sequences */}
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-sequences"
        component={EmailSequenceList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-sequences/create"
        component={EmailSequenceCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-sequences/:id/update"
        component={EmailSequenceUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/email/email-sequences/:id/analytics"
        component={EmailSequenceAnalytics}
      />

      {/* Pulse Reports */}
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports"
        component={PulseReportsAnalyticsDashboard}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/list"
        component={PulseReportsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/create"
        component={PulseReportCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/:id/edit"
        component={PulseReportEdit}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/:id/stats"
        component={PulseReportStats}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/:id/recipients"
        component={PulseReportRecipients}
      />
      {/* Pulse Report HTML Templates */}
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/html-templates"
        component={PulseHtmlTemplatesList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/html-template/:id/create"
        component={PulseHtmlTemplateBuilder}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/pulse-reports/html-template/:id/update"
        component={PulseHtmlTemplateBuilder}
      />

      {/* SEO */}
      {/* AI Apps Categories */}
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-apps/ai-app-categories"
        component={AiAppCategoriesList}
      />

      {/* AI Apps List */}
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-apps"
        component={AiAppsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-app/create"
        component={AiAppCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-app/:id/update"
        component={AiAppUpdate}
      />

      {/* AI Glossary Categories */}
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-glossary/ai-glossary-categories"
        component={AiGlossaryCategoriesList}
      />

      {/* AI Glossary List */}
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-glossary"
        component={AiGlossaryList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-glossary/create"
        component={AiGlossaryCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-glossary/:id/update"
        component={AiGlossaryUpdate}
      />

      {/* AI Tools */}
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-tools"
        component={AiToolsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-tool/create"
        component={AiToolCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-tool/:id/update"
        component={AiToolUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/ai-tools/ai-tool-categories"
        component={AiToolCategoriesList}
      />

      {/* Programmatic SEO */}
      <SuperAdminRoute
        exact
        path="/super-admin/seo/programmatic-seo/campaigns"
        component={ProgrammaticSeoCampaigns}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/programmatic-seo/dashboard"
        component={ProgrammaticSeoDashboard}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/programmatic-seo/analytics"
        component={ProgrammaticSeoAnalytics}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/seo/programmatic-seo/pages"
        component={ProgrammaticSeoPages}
      />

      {/* Constants */}
      <SuperAdminRoute
        exact
        path="/super-admin/constants"
        component={ConstantsList}
      />
      {/* Announcements */}
      <SuperAdminRoute
        exact
        path="/super-admin/announcements"
        component={AnnouncementsList}
      />

      {/******************** COPILOTS ********************************* //}
      {/* Unified Copilots Dashboard */}
      <SuperAdminRoute
        exact
        path="/super-admin/copilots"
        component={CopilotsDashboard}
      />

      {/* Legacy Routes - Redirect to Dashboard */}
      <Redirect
        from="/super-admin/copilots/browser-extension/categories"
        to="/super-admin/copilots?platform=browser&section=categories"
      />
      <Redirect
        from="/super-admin/copilots/browser-extension/copilots"
        to="/super-admin/copilots?platform=browser&section=prompts"
      />
      <Redirect
        from="/super-admin/copilots/browser-extension/copilot-chats"
        to="/super-admin/copilots?platform=browser&section=chats"
      />
      <Redirect
        from="/super-admin/copilots/mobile-app/categories"
        to="/super-admin/copilots?platform=mobile&section=categories"
      />
      <Redirect
        from="/super-admin/copilots/mobile-app/copilot-chats"
        to="/super-admin/copilots?platform=mobile&section=chats"
      />

      {/* Settings */}
      <SuperAdminRoute
        exact
        path="/super-admin/settings/ai-config"
        component={AiConfig}
      />

      {/* Plans */}
      <SuperAdminRoute exact path="/super-admin/plans" component={PlansList} />
      <SuperAdminRoute
        exact
        path="/super-admin/plans/create"
        component={PlanCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/plans/update/:id"
        component={PlanUpdate}
      />
      {/* Coupons */}
      <SuperAdminRoute
        exact
        path="/super-admin/coupons"
        component={CouponsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/coupons/create"
        component={CouponCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/coupons/update/:id"
        component={CouponUpdate}
      />

      {/* Promotions */}
      <SuperAdminRoute
        exact
        path="/super-admin/promotions/analytics"
        component={PromotionsAnalyticsDashboard}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/promotions/list"
        component={PromotionsList}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/promotions/create"
        component={PromotionCreate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/promotions/update/:id"
        component={PromotionUpdate}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/promotions/:id/analytics"
        component={PromotionAnalytics}
      />

      {/* Credit Rewards */}
      <SuperAdminRoute
        exact
        path="/super-admin/credits/config"
        component={CreditConfig}
      />
      <SuperAdminRoute
        exact
        path="/super-admin/credits/analytics"
        component={CreditAnalytics}
      />

      {/* Analytics Dashboard */}
      <SuperAdminRoute
        exact
        path="/super-admin/analytics"
        component={SuperAdminAnalyticsDashboard}
      />

      {/*************** Marketing Admin Routes ***************/}
      {/* Dashboard */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/dashboard"
        component={MarketingAdminDashboard}
      />
      {/* Visitors */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/visitors"
        component={VisitorList}
      />
      {/* Leads */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/leads"
        component={LeadsList}
      />

      {/* Blogs */}
      {/* Blog Categories */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/blogs/blog-categories"
        component={BlogCategoriesList}
      />
      {/* Blog Tags */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/blogs/tags"
        component={BlogTagsList}
      />
      {/* Blogs List */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/blogs"
        component={BlogsList}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/blog/create"
        component={BlogCreate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/blog/:id/update"
        component={BlogUpdate}
      />

      {/* Emails */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/analytics"
        component={EmailAnalyticsDashboard}
      />
      {/* Email Campaigns */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/email-campaigns"
        component={EmailCampaignsList}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/email-campaigns/create"
        component={EmailCampaignCreate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/email-campaigns/:id/update"
        component={EmailCampaignUpdate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/email-campaigns/:id/analytics"
        component={EmailCampaignAnalytics}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/email-campaigns/:id/sent-emails"
        component={EmailCampaignSentEmails}
      />
      {/* HTML Templates */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/html-templates"
        component={HtmlTemplatesList}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/html-template/:id/create"
        component={HtmlTemplateBuilder}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/email/html-template/:id/update"
        component={HtmlTemplateBuilder}
      />

      {/* SEO */}
      {/* AI Apps Categories */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-apps/ai-app-categories"
        component={AiAppCategoriesList}
      />

      {/* AI Apps List */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-apps"
        component={AiAppsList}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-app/create"
        component={AiAppCreate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-app/:id/update"
        component={AiAppUpdate}
      />

      {/* AI Glossary Categories */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-glossary/ai-glossary-categories"
        component={AiGlossaryCategoriesList}
      />

      {/* AI Glossary List */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-glossary"
        component={AiGlossaryList}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-glossary/create"
        component={AiGlossaryCreate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-glossary/:id/update"
        component={AiGlossaryUpdate}
      />

      {/* AI Tools */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-tools"
        component={AiToolsList}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-tool/create"
        component={AiToolCreate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-tool/:id/update"
        component={AiToolUpdate}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/ai-tools/ai-tool-categories"
        component={AiToolCategoriesList}
      />

      {/* Programmatic SEO */}
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/programmatic-seo/campaigns"
        component={ProgrammaticSeoCampaigns}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/programmatic-seo/dashboard"
        component={ProgrammaticSeoDashboard}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/programmatic-seo/analytics"
        component={ProgrammaticSeoAnalytics}
      />
      <MarketingAdminRoute
        exact
        path="/marketing-admin/seo/programmatic-seo/pages"
        component={ProgrammaticSeoPages}
      />

      {/**************** Admin Routes ****************/}
      {/* Dashboard */}
      <AdminRoute
        exact
        path="/a/ws/:wid/dashboard"
        component={AdminDashboard}
      />
      {/* Generations History */}
      <AdminRoute
        exact
        path="/a/ws/:wid/history/generations"
        component={GenerationHistoryList}
      />
      <AdminRoute
        exact
        path="/a/ws/:wid/history/chats"
        component={ChatHistoryList}
      />
      <AdminRoute
        exact
        path="/a/ws/:wid/history/generation/:id"
        component={GenerationDetails}
      />
      <AdminRoute
        exact
        path="/a/ws/:wid/history/chat/:id"
        component={ChatDetails}
      />
      {/* Trash */}
      <AdminRoute
        exact
        path="/a/ws/:wid/trash/generations"
        component={GenerationTrashList}
      />
      <AdminRoute
        exact
        path="/a/ws/:wid/trash/chats"
        component={ChatTrashList}
      />
      {/* Team */}
      <AdminRoute exact path="/a/ws/:wid/team" component={TeamList} />
      <AdminRoute
        exact
        path="/a/ws/:wid/team/analytics"
        component={TeamAnalytics}
      />

      {/* Prompts */}
      <AdminRoute
        exact
        path="/a/ws/:wid/prompts"
        component={AdminPromptsList}
      />

      {/* Chats */}
      <AdminRoute exact path="/a/ws/:wid/chats" component={Chats} />

      {/* Chats (Bot Selected) */}
      <AdminRoute exact path="/a/ws/:wid/chats/bot/:bid" component={Chats} />

      {/* Chats (Bot + Chat Selected) */}
      <AdminRoute
        exact
        path="/a/ws/:wid/chats/bot/:bid/chat/:cid"
        component={Chats}
      />
      {/* Copilotly Apps */}
      <AdminRoute exact path="/a/ws/:wid/apps" component={CopilotlyApps} />
      {/* Subscription */}
      <AdminRoute exact path="/success" component={SubscriptionSuccess} />
      <AdminRoute exact path="/cancel" component={PaymentCancel} />

      {/*************** Profile Routes ***************/}
      <ProfileRoute exact path="/profile/information" component={MyProfile} />
      <ProfileRoute exact path="/profile/plans" component={SubscriptionPlans} />
      <ProfileRoute exact path="/profile/security" component={MySecurity} />
      <ProfileRoute exact path="/profile/password" component={MyPassword} />
      <ProfileRoute
        exact
        path="/profile/subscription"
        component={MySubscription}
      />
      <ProfileRoute exact path="/profile/language" component={ChangeLanguage} />
    </Switch>
  );
};

export default App;
